import {
  FormControl,
  Button,
  Box,
  Card,
  CardContent,
  Typography,
  useMediaQuery,
  Alert,
  AlertTitle,
  Fab,
  Grid,
  Pagination,
} from "@mui/material";
import { FormEvent, useEffect, useState } from "react";
import { useCharactersContext } from "../hooks/Context";
import LayoutWithIcons from "./LayoutWithIcons";

interface Question {
  updatedBy: string;
  question: string;
  mediaUrl: string;
  difficultyLevel: string;
  answerType: number;
  active: boolean;
  marks: number;
  updatedDate: string;
  subjectId: number;
  questionBankId: number;
  classId: number;
  createdDate: string;
  createdBy: string;
  optionsList: { optionId: number; option: string; correctAnswer: string }[];
  questionPaperId: number;
  uiType: number;
  remarks: string;
}

interface LayoutProps {
  Questions: Question[];
  sectionName: string;
  currentQuestionIndexProp?: number;
}

const Layout18: React.FC<LayoutProps> = ({
  Questions,
  sectionName,
  currentQuestionIndexProp,
}) => {
  const [internalQuestionIndex, setInternalQuestionIndex] = useState(0);
  
  // Use prop index if provided (grammar mode), otherwise use internal state (standalone mode)
  const currentQuestionIndex: number = currentQuestionIndexProp !== undefined 
    ? currentQuestionIndexProp 
    : internalQuestionIndex;
  
  const isGrammarMode = currentQuestionIndexProp !== undefined;
  const {
    questionScores,
    setQuestionScores,
    isViewMode,
    examAnswers,
    setExamAnswers,
  } = useCharactersContext();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isTablet = useMediaQuery("(max-width:820px)");
  const [showAlert, setShowAlert] = useState(false);
  const handleAlertClose = () => setShowAlert(false);
  setTimeout(handleAlertClose, 3000);

  const handleNavigate = (direction: "previous" | "next") => {
    if (!isGrammarMode) {
      if (direction === "previous") {
        setInternalQuestionIndex((prevIndex) => Math.max(prevIndex - 1, 0));
      } else if (direction === "next") {
        setInternalQuestionIndex((prevIndex) =>
          Math.min(prevIndex + 1, Questions.length - 1)
        );
      }
    }
  };

  const handlePaginationChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    if (!isGrammarMode) {
      setInternalQuestionIndex(value - 1);
    }
  };

  const currentQuestion = Questions[currentQuestionIndex];
  const sentences = currentQuestion.question.split("<br>");
  const options = currentQuestion?.optionsList ?? [];
  const correctOptions = options
    .filter((option) => option.correctAnswer)
    .map((option) => option.option);

  const [selectedOptions, setSelectedOptions] = useState<string[][]>(() => {
    if (!currentQuestion) return Array(Questions.length).fill([]);
    const storageKey = `selectedOptionsLayout18_${sectionName}_${currentQuestion.questionPaperId}`;
    const savedOptions = localStorage.getItem(storageKey);
    return savedOptions
      ? JSON.parse(savedOptions)
      : Array(Questions.length).fill([]);
  });

  const handleOptionChange = (option: string) => {
    setSelectedOptions((prevSelectedOptions) => {
      const updatedOptions = [...prevSelectedOptions];
      const currentOptions = updatedOptions[currentQuestionIndex];

      const isSingleCorrectAnswer = correctOptions.length === 1;
      const isHighlightedWordType = currentQuestion.answerType === 1 && sentences[1] && sentences[2];

      if (sectionName === "Syllabification" || isSingleCorrectAnswer || isHighlightedWordType) {
        updatedOptions[currentQuestionIndex] = [option];
      } else {
        if (currentOptions.includes(option)) {
          updatedOptions[currentQuestionIndex] = currentOptions.filter(
            (item: string) => item !== option
          );
        } else {
          updatedOptions[currentQuestionIndex] = [...currentOptions, option];
        }
      }

      return updatedOptions;
    });
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setShowAlert(true);

    const selectedForCurrentQuestion = selectedOptions[currentQuestionIndex];
    const isCorrect = correctOptions.every((option) =>
      selectedForCurrentQuestion.includes(option)
    );

    if (isCorrect) {
      const questionIndex = questionScores.findIndex(
        (item) => item.questionBankId === currentQuestion.questionBankId
      );

      if (questionIndex !== -1) {
        const updatedScores = [...questionScores];
        updatedScores[questionIndex] = {
          questionBankId: currentQuestion.questionBankId,
          questionPaperId: currentQuestion.questionPaperId,
          score: currentQuestion.marks,
          layoutNumber: currentQuestion.uiType,
          sectionName: sectionName,
        };
        setQuestionScores(updatedScores);
      } else {
        setQuestionScores((prevScores) => [
          ...prevScores,
          {
            questionBankId: currentQuestion.questionBankId,
            questionPaperId: currentQuestion.questionPaperId,
            score: currentQuestion.marks,
            layoutNumber: currentQuestion.uiType,
            sectionName: sectionName,
          },
        ]);
      }
    } else {
      setQuestionScores((prevScores) => [
        ...prevScores,
        {
          questionBankId: currentQuestion.questionBankId,
          questionPaperId: currentQuestion.questionPaperId,
          score: 0,
          layoutNumber: currentQuestion.uiType,
          sectionName: sectionName,
        },
      ]);
    }

    const updatedExamAnswer = {
      questionBankId: currentQuestion.questionBankId,
      acutalOptionValue: correctOptions.join(","),
      enteredOptionValue: selectedOptions[currentQuestionIndex].join(","),
    };

    const existingAnswerIndex = examAnswers.findIndex(
      (answer) => answer.questionBankId === currentQuestion.questionBankId
    );

    if (existingAnswerIndex !== -1) {
      setExamAnswers((prevAnswers) =>
        prevAnswers.map((answer, index) =>
          index === existingAnswerIndex ? { ...updatedExamAnswer } : answer
        )
      );
    } else {
      setExamAnswers((prevAnswers) => [...prevAnswers, updatedExamAnswer]);
    }
  };

  useEffect(() => {
    if (currentQuestion) {
      const storageKey = `selectedOptionsLayout18_${sectionName}_${currentQuestion.questionPaperId}`;
      localStorage.setItem(storageKey, JSON.stringify(selectedOptions));
    }
  }, [selectedOptions, sectionName, currentQuestion?.questionPaperId]);

  useEffect(() => {
    if (isViewMode) {
      const newSelectedOptions = Array(Questions.length).fill(correctOptions);
      setSelectedOptions(newSelectedOptions);
    }
  }, [currentQuestion, isViewMode, Questions.length, correctOptions]);

  const cardContent = (
    <>
      {showAlert && (
        <Alert
          severity="success"
          variant="filled"
          sx={{
            position: "fixed",
            top: "30%",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            padding: "0.5",
          }}
        >
          <AlertTitle sx={{ color: "white" }}>Answer submitted</AlertTitle>
        </Alert>
      )}
      {currentQuestion && (
        <Card
          variant="outlined"
          sx={{
            borderRadius: "12px",
            boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
            minWidth: "40vw",
            pointerEvents: isViewMode ? "none" : "auto",
          }}
        >
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                paddingBottom: "0px",
              }}
            >
              {/* Question line 1 */}
              <Typography
                sx={{
                  fontWeight: "700",
                  fontSize: isMobile ? "100%" : "110%",
                  textAlign: "center",
                  marginBottom: "10px",
                }}
              >
                {sentences[0]}
              </Typography>

              {/* Image context (e.g. Grammar questions with mediaUrl) */}
              {currentQuestion.mediaUrl ? (
                <Box
                  component="img"
                  src={currentQuestion.mediaUrl}
                  alt="question context"
                  sx={{
                    maxWidth: isMobile ? "80vw" : "40vw",
                    maxHeight: "30vh",
                    objectFit: "contain",
                    borderRadius: "8px",
                    mb: 1,
                  }}
                />
              ) : null}

              {/* For answerType 1 with 3 lines: Display sentence with highlighted word, otherwise use FAB */}
              {currentQuestion.answerType === 1 && sentences[1] && sentences[2] ? (
                <Typography
                  sx={{
                    fontWeight: "450",
                    fontSize: isMobile ? "100%" : "110%",
                    margin: "1vh",
                    textAlign: "center",
                    padding: "12px 20px",
                  }}
                  component="div"
                >
                  {(() => {
                    const wordToHighlight = sentences[2].trim();
                    // Escape special regex characters
                    const escapedWord = wordToHighlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
                    const regex = new RegExp(`(${escapedWord})`, "gi");
                    const parts = sentences[1].split(regex);
                    return parts.map((part, index) => {
                      const isHighlighted = part.toLowerCase() === wordToHighlight.toLowerCase();
                      return (
                        <span
                          key={index}
                          style={{
                            color: isHighlighted ? "#f44336" : "inherit",
                            fontWeight: isHighlighted ? "700" : "inherit",
                          }}
                        >
                          {part}
                        </span>
                      );
                    });
                  })()}
                </Typography>
              ) : (
                <>
                  {/* FAB with question line 2 */}
                  {sentences[1] && (
                    <Fab
                      variant="extended"
                      sx={{
                        fontWeight: 600,
                        fontSize: isMobile ? "10px" : "auto",
                        cursor: "default",
                        fontFamily: '"Nunito","Segoe UI", sans-serif',
                        backgroundColor: "#E12027 !important",
                        color: "white",
                        p: 1.5,
                        m: 1,
                        boxShadow: "none",
                      }}
                    >
                      {sentences[1].trim()}
                    </Fab>
                  )}

                  {/* Question line 3 */}
                  {sentences[2] && (
                    <Typography
                      sx={{
                        fontWeight: "450",
                        fontSize: isMobile ? "100%" : "110%",
                        margin: "1vh",
                        textAlign: "center",
                      }}
                    >
                      {sentences[2].trim()}
                    </Typography>
                  )}
                </>
              )}
            </Box>

            {/* Options */}
            <form onSubmit={handleFormSubmit}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 2,
                }}
              >
                <FormControl>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      flexWrap: "wrap",
                      gap: "8px",
                      ...(isMobile && {
                        gap: "0px",
                      }),
                    }}
                  >
                    {options.map((option) => (
                      <Button
                        key={option.optionId}
                        onClick={() => handleOptionChange(option.option)}
                        variant={
                          selectedOptions[currentQuestionIndex].includes(
                            option.option
                          )
                            ? "contained"
                            : "outlined"
                        }
                        sx={{
                          m: 1,
                          backgroundColor: selectedOptions[
                            currentQuestionIndex
                          ].includes(option.option)
                            ? "#E12027"
                            : undefined,
                          "&:hover": {
                            backgroundColor: selectedOptions[
                              currentQuestionIndex
                            ].includes(option.option)
                              ? "#E12027"
                              : undefined,
                          },
                          ...(isMobile && {
                            padding: "0.1vh 0.5vh",
                            minWidth: "1vh",
                            minHeight: "1vh",
                          }),
                        }}
                      >
                        {option.option}
                      </Button>
                    ))}
                  </Box>
                </FormControl>
              </Box>
              {!isViewMode && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 2,
                  }}
                >
                  <Button type="submit" variant="contained">
                    Submit
                  </Button>
                </Box>
              )}
            </form>
          </CardContent>
        </Card>
      )}
    </>
  );

  if (isGrammarMode) {
    return cardContent;
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginTop:
          isMobile && isViewMode
            ? "17%"
            : isTablet
            ? "20%"
            : isMobile
            ? "10%"
            : isViewMode
            ? "6%"
            : "4%",
        marginBottom: "auto",
      }}
    >
      <LayoutWithIcons onNavigate={handleNavigate}>
        {cardContent}
        <Grid container lg={12} md={12} sm={12} xs={12} justifyContent="center">
          <Pagination
            count={Questions.length}
            shape="rounded"
            sx={{
              marginBottom: "1vh",
              paddingTop: "4%",
            }}
            page={currentQuestionIndex + 1}
            onChange={handlePaginationChange}
          />
        </Grid>
      </LayoutWithIcons>
    </div>
  );
};

export default Layout18;

